// Single REST client for the bourso API gateway (TS Express on :3005).
// The TS gateway re-exposes the Go API under /api/go/* so the frontend
// only ever talks to one origin.
//
//   /api/go/*    → proxied to Go API (read-only data : forums, threads, users, stats)
//   /api/*       → native TS endpoints (auth, follows, billing, ...)
//
// In dev, Vite proxies /api → http://localhost:3005. In prod, set VITE_API_URL.

const BASE = import.meta.env.VITE_API_URL ?? "";

// Better Auth sessions normally ride on a HttpOnly cookie, but some browsers
// (Brave, Safari with ITP) reject same-site cookies on localhost proxies. As
// a fallback we also stash the session token in localStorage and resend it
// as `Authorization: Bearer ...`. Better Auth recognises this header.
const TOKEN_KEY = "bourso.auth_token";

export function setAuthToken(token: string | null) {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

export function getAuthToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

class APIError extends Error {
  constructor(public status: number, message: string, public body?: unknown) {
    super(message);
  }
}

async function http<T>(path: string, init?: RequestInit): Promise<T> {
  const token = getAuthToken();
  const res = await fetch(`${BASE}/api${path}`, {
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init?.headers ?? {}),
    },
    credentials: "include",
    ...init,
  });
  if (!res.ok) {
    let body: unknown;
    try { body = await res.json(); } catch { /* noop */ }
    throw new APIError(res.status, res.statusText, body);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

// Shortcut for endpoints proxied to the Go API.
const go = <T>(path: string, init?: RequestInit) => http<T>(`/go${path}`, init);

export type Forum = {
  id: number;
  slug: string;
  url: string;
  title: string;
  category: string | null;
  active_score: number | null;
  threads_30d: number | null;
  replies_30d: number | null;
  last_reply_at: string | null;
  last_thread_at: string | null;
  scrape_interval_sec: number;
  dead: boolean;
  // Latest cotation snapshot (stocks/indices forums only ; null for thematic ones).
  last_price: number | null;
  last_variation_pct: number | null;
  price_currency: string | null;
  isin: string | null;
  symbol: string | null;
  last_price_at: string | null;
  // Windowed activity counts + same-duration previous window for trend %
  // computation (e.g. threads_24h / threads_24h_prev → +35 % vs hier).
  // Always present as integers (0 when no activity).
  threads_24h: number;
  threads_24h_prev: number;
  threads_7d: number;
  threads_7d_prev: number;
  posts_24h: number;
  posts_24h_prev: number;
  posts_7d: number;
  posts_7d_prev: number;
};

export type BoursoUser = {
  id: number;
  username: string;
  slug: string | null;
  posts_count: number;
  last_seen_at: string | null;
  member_since: string | null;
  boursostars_stars: number | null;
  posts_created_count: number | null;
  likes_received_count: number | null;
  following_count: number | null;
  followers_count: number | null;
};

export type NewsItem = {
  id: number;
  forum_id: number;
  url: string;
  title: string;
  source: string;
  description: string;
  published_at: string;
  forum_slug: string;
  forum_title: string;
  active_score: number | null;
  dead: boolean;
  // Daily close for the publication date (null when unknown).
  price_at_publish: number | null;
  variation_at_publish: number | null;
  price_currency: string | null;
  price_source: "daily" | null;
};

export type NewsFeed = {
  items: NewsItem[];
  next_cursor: string;
};

export type SignalWindow = "1h" | "4h" | "24h";

export type Signal = {
  forum_id: number;
  slug: string;
  title: string;
  posts_recent: number;
  authors_recent: number;
  /** Authors whose account was first seen < 7 days ago (pump suspicion hint). */
  new_authors: number;
  last_post_at: string | null;
  /** Average posts per window over the previous 7 days. */
  baseline: number;
  /** posts_recent / baseline — null when the forum has no 7-day history. */
  burst_ratio: number | null;
  category: string;
  active_score: number | null;
  last_price: number | null;
  last_variation_pct: number | null;
  price_currency: string | null;
  isin: string | null;
  symbol: string | null;
  last_price_at: string | null;
};

export type Plan = "free" | "pro";

export type AlertKind = "new_thread" | "new_post" | "thread_revived";

export type Alert = {
  id: number;
  kind: AlertKind;
  thread_id: number | null;
  post_id: number | null;
  follow_id: number | null;
  orphan: boolean;
  payload: unknown;
  created_at: string;
  read_at: string | null;
  thread_title: string | null;
  thread_url: string | null;
  forum_id: number | null;
  forum_slug: string | null;
  forum_title: string | null;
  follow_target_type: "forum" | "user" | "thread" | null;
  follow_target_id: number | null;
};

export type Follow = {
  id: number;
  target_type: "forum" | "user" | "thread";
  target_id: number;
  created_at: string;
};

export type Thread = {
  id: number;
  external_id: string;
  url: string;
  title: string;
  replies_count: number;
  views: number;
  last_post_at: string | null;
  first_seen_at: string | null;
};

/** Single-thread metadata, returned by GET /threads/:id. */
export type ThreadMeta = {
  id: number;
  title: string;
  url: string;
  external_id: string;
  replies_count: number | null;
  views: number | null;
  last_post_at: string | null;
  first_seen_at: string | null;
  forum_id: number;
  forum_slug: string;
  forum_title: string;
  price_currency: string | null;
};

export type Post = {
  id: number;
  author_id: number | null;
  username: string;
  content: string;
  posted_at: string;
  position: number | null;
  author_member_since: string | null;
  author_first_seen_at: string | null;
  author_posts_count: number | null;
  author_stars: number | null;
  price_at_post: number | null;
  variation_at_post: number | null;
  price_currency: string | null;
  price_source: "scrape" | "daily" | null;
};

export type MeUser = {
  id: number;
  email: string;
  name: string | null;
  onboarding_completed: boolean;
  bourso_user_id: number | null;
  usage_profile: "poster" | "watcher" | "both" | null;
  llm_opt_in: boolean;
  digest_enabled: boolean;
  newsletter_opt_in: boolean;
  // Monetization. `plan` is the gating source of truth ; status/renews are for
  // display only. Older API builds (pre-billing) omit these — default to free.
  plan: Plan;
  plan_status: string | null;
  plan_renews_at: string | null;
  // Admin allow-list (ADMIN_EMAILS on the gateway). Drives the /admin nav entry.
  is_admin?: boolean;
};

export type BillingInterval = "monthly" | "yearly";

export type Preferences = {
  digest_enabled: boolean;
  newsletter_opt_in: boolean;
  llm_opt_in: boolean;
  usage_profile: "poster" | "watcher" | "both" | null;
};

export type AdminSignupStats = {
  total: number;
  last_7d: number;
  prev_7d: number;
  last_30d: number;
  onboarding_completed: number;
  pro: number;
  daily_30d: { day: string; count: number }[];
  weekly_12w: { week_start: string; count: number }[];
  recent: {
    id: string;
    email: string;
    created_at: string;
    onboarding_completed: boolean;
    plan: string | null;
  }[];
  generated_at: string;
};

export const api = {
  // ── Admin (allow-listed emails only) ──
  adminSignupStats: () => http<AdminSignupStats>("/admin/stats/users"),
  adminSendRecap: () =>
    http<{ sent_to: string; last_7d: number; total: number }>("/admin/recap/send", {
      method: "POST",
    }),
  // Auth (native TS)
  me: () => http<{ user: MeUser | null }>("/me"),

  // Profile / onboarding (native TS)
  linkBourso: (boursoUserId: number) =>
    http<{ bourso_user_id: number }>("/me/link-bourso", {
      method: "POST",
      body: JSON.stringify({ bourso_user_id: boursoUserId }),
    }),
  unlinkBourso: () =>
    http<{ bourso_user_id: null }>("/me/link-bourso", { method: "DELETE" }),
  completeOnboarding: () =>
    http<{ onboarding_completed: boolean }>("/me/onboarding/complete", {
      method: "POST",
    }),
  deleteAccount: () => http<void>("/me", { method: "DELETE" }),

  // Aggregated cross-forum news feed (proxied to Go). Keyset pagination via
  // `before` (RFC3339 cursor returned as next_cursor).
  news: (opts: { category?: string; forumId?: number; limit?: number; before?: string } = {}) => {
    const params = new URLSearchParams();
    if (opts.category && opts.category !== "all") params.set("category", opts.category);
    if (opts.forumId != null) params.set("forum_id", String(opts.forumId));
    if (opts.limit != null) params.set("limit", String(opts.limit));
    if (opts.before) params.set("before", opts.before);
    const qs = params.toString();
    return go<NewsFeed>(`/news${qs ? "?" + qs : ""}`);
  },

  // Activity→price signals : forums bursting above their own baseline.
  signals: (window: SignalWindow = "4h", limit = 20) =>
    go<{ window: SignalWindow; items: Signal[] }>(`/signals?window=${window}&limit=${limit}`),

  // Read endpoints (proxied to Go API)
  forums: (opts: { category?: string; limit?: number } = {}) => {
    const params = new URLSearchParams();
    if (opts.category && opts.category !== "all" && opts.category !== "followed") {
      params.set("category", opts.category);
    }
    if (opts.limit != null) params.set("limit", String(opts.limit));
    const qs = params.toString();
    return go<Forum[]>(`/forums${qs ? "?" + qs : ""}`);
  },
  threads: (forumId: number | string) => go<Thread[]>(`/forums/${forumId}/threads`),
  thread: (threadId: number | string) => go<ThreadMeta>(`/threads/${threadId}`),
  posts: (threadId: number | string) => go<Post[]>(`/threads/${threadId}/posts`),
  users: (q = "") => go<BoursoUser[]>(`/users?q=${encodeURIComponent(q)}`),
  user: (id: number | string) => go<any>(`/users/${id}`),
  userActivity: (id: number | string) => go<any[]>(`/users/${id}/activity`),
  userHistory: (id: number | string) => go<any[]>(`/users/${id}/history`),
  userFollowGraph: (id: number | string) => go<{ following: any[]; followers: any[] }>(`/users/${id}/follow-graph`),

  // Follows (native TS — auth-protected ; returns [] when not authenticated)
  follows: async (): Promise<Follow[]> => {
    try {
      return await http<Follow[]>("/follows");
    } catch (e) {
      if (e instanceof APIError && e.status === 401) return [];
      throw e;
    }
  },
  createFollow: (body: { target_type: "forum" | "user" | "thread"; target_id: number }) =>
    http<{ id: number }>("/follows", { method: "POST", body: JSON.stringify(body) }),
  deleteFollow: (id: number) => http<void>(`/follows/${id}`, { method: "DELETE" }),

  // Alerts (proxied to Go). The acting user is resolved server-side from the
  // auth session (X-User-Id header minted by the gateway) — never sent by the
  // client.
  alerts: () => go<Alert[]>("/alerts"),
  markRead: (id: number) => go<void>(`/alerts/${id}/read`, { method: "POST" }),
  markAllRead: () => go<{ updated: number }>("/alerts/read-all", { method: "POST" }),

  // Notification & profile preferences (native TS). All fields optional —
  // only the provided ones are updated.
  updatePreferences: (body: Partial<Preferences>) =>
    http<Preferences>("/me/preferences", {
      method: "PATCH",
      body: JSON.stringify(body),
    }),

  // On-demand refresh (queued in Redis, scraper picks it up)
  refreshForum: (id: number | string) =>
    go<{ queued: boolean; job: any }>(`/refresh/forum/${id}`, { method: "POST" }),
  refreshThread: (id: number | string) =>
    go<{ queued: boolean; job: any }>(`/refresh/thread/${id}`, { method: "POST" }),
  refreshUser: (id: number | string) =>
    go<{ queued: boolean; job: any }>(`/refresh/user/${id}`, { method: "POST" }),
  refreshTopPrices: (limit = 50) =>
    go<{ queued: number; limit: number }>(`/refresh/prices?limit=${limit}`, { method: "POST" }),

  /**
   * Synchronous price refresh : the Go API fetches Boursorama inline,
   * persists the new snapshot, and returns the fresh price right away —
   * no queue, no polling needed.
   */
  refreshForumPriceSync: (id: number | string) =>
    go<{
      forum_id: number;
      has_price: boolean;
      title?: string;
      last_price?: number;
      last_variation_pct?: number;
      price_currency?: string;
      isin?: string;
      symbol?: string;
      observed_at: string;
    }>(`/forums/${id}/refresh-price`, { method: "POST" }),

  // Cotation history (one forum)
  forumPrices: (id: number | string) =>
    go<Array<{ observed_at: string; price: number; variation_pct: number; currency: string }>>(
      `/forums/${id}/prices`
    ),

  // News + daily OHLC
  forumNews: (id: number | string) =>
    go<
      Array<{
        id: number;
        url: string;
        title: string;
        source: string;
        description: string;
        published_at: string;
        // Daily close for the publication date (null when unknown).
        price_at_publish: number | null;
        variation_at_publish: number | null;
        price_currency: string | null;
        price_source: "daily" | null;
      }>
    >(`/forums/${id}/news`),

  refreshForumNewsSync: (id: number | string) =>
    go<{
      forum_id: number;
      has_news_page: boolean;
      items: Array<{
        url: string;
        title: string;
        source: string;
        description: string;
        published_at: string;
      }>;
    }>(`/forums/${id}/refresh-news`, { method: "POST" }),

  forumHistory: (id: number | string) =>
    go<
      Array<{
        session_date: string;
        last_price: number;
        variation_pct: number | null;
        high_price: number | null;
        low_price: number | null;
        open_price: number | null;
      }>
    >(`/forums/${id}/history`),

  refreshForumHistorySync: (id: number | string) =>
    go<{
      forum_id: number;
      has_history: boolean;
      history_url?: string;
      rows: Array<{
        session_date: string;
        last_price: number;
        variation_pct: number | null;
        high_price: number | null;
        low_price: number | null;
        open_price: number | null;
      }>;
    }>(`/forums/${id}/refresh-history`, { method: "POST" }),

  /**
   * All-in-one : refreshes price, news AND history in a single backend call.
   * The Go API fetches Boursorama in parallel and persists everything ; the
   * frontend can write the response straight into its React Query caches.
   */
  refreshForumAllSync: (id: number | string) =>
    go<{
      forum_id: number;
      price: {
        has_price: boolean;
        title: string;
        last_price: number;
        last_variation_pct: number;
        price_currency: string;
        isin: string;
        symbol: string;
        observed_at: string;
      };
      news: {
        has_news_page: boolean;
        items: Array<{
          url: string;
          title: string;
          source: string;
          description: string;
          published_at: string;
        }>;
      };
      history: {
        has_history: boolean;
        history_url?: string;
        rows: Array<{
          session_date: string;
          last_price: number;
          variation_pct: number | null;
          high_price: number | null;
          low_price: number | null;
          open_price: number | null;
        }>;
      };
    }>(`/forums/${id}/refresh-all`, { method: "POST" }),

  // Stats (proxied to Go).
  //
  // The handlers now all accept ?window=24h|48h|7d|14d and return an
  // envelope { window, data }. We unwrap to `data` on the client so
  // consumers see a flat array, and expose the windowed responses on
  // separate methods named statsXxxWindowed when the wrapper info matters.
  statsForums: (window: StatsWindow = "24h") =>
    go<StatsEnvelope<any[]>>(`/stats/active-forums?window=${window}`).then(unwrap),
  statsUsers: (window: StatsWindow = "24h") =>
    go<StatsEnvelope<any[]>>(`/stats/active-users?window=${window}`).then(unwrap),
  statsForumsByScore: (window: StatsWindow = "24h") =>
    go<StatsEnvelope<any[]>>(`/stats/forums-by-score?window=${window}`).then(unwrap),
  statsCategories: (window: StatsWindow = "24h") =>
    go<StatsEnvelope<{ category: string; count: number }[]>>(
      `/stats/categories?window=${window}`
    ).then(unwrap),
  statsHeatmap: (window: StatsWindow = "24h") =>
    go<StatsEnvelope<{ day_of_week: number; hour_of_day: number; posts: number }[]>>(
      `/stats/heatmap?window=${window}`
    ).then(unwrap),
  statsBlocked: () => go<any[]>("/stats/blocked"),
  statsOverview: (window: StatsWindow = "24h") =>
    go<{
      window: StatsWindow;
      total_forums: number;
      total_users: number;
      total_threads: number;
      total_posts: number;
      posts_window: number;
      threads_window: number;
      active_users_window: number;
      hot_forums: number;
      dead_forums: number;
    }>(`/stats/overview?window=${window}`),
  statsPostsTimeline: (window: StatsWindow = "24h") =>
    go<{
      window: StatsWindow;
      bucket_seconds: number;
      data: { ts: string; posts: number; unique_users: number; unique_threads: number }[];
    }>(`/stats/posts-timeline?window=${window}`),
  statsTopThreads: (window: StatsWindow = "24h") =>
    go<StatsEnvelope<any[]>>(`/stats/top-threads?window=${window}`).then(unwrap),

  // ─────────────────────── LLM (BYOK) ───────────────────────
  // Native TS endpoints (the key never reaches the Go API).
  llmKeyStatus: () => http<LlmKeyStatus>("/llm/key"),
  saveLlmKey: (body: { provider: LlmProvider; key: string; model?: string }) =>
    http<{ ok: boolean; provider: LlmProvider; model: string; last4: string }>("/llm/key", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  deleteLlmKey: (provider: LlmProvider) =>
    http<void>(`/llm/key?provider=${provider}`, { method: "DELETE" }),

  // ─────────────────────── Billing (Stripe) ───────────────────────
  // checkout → returns a Stripe Checkout URL to redirect to. portal → returns
  // the Billing Portal URL (manage / cancel).
  billingCheckout: (interval: BillingInterval = "monthly") =>
    http<{ url: string }>("/billing/checkout", {
      method: "POST",
      body: JSON.stringify({ interval }),
    }),
  billingPortal: () => http<{ url: string }>("/billing/portal", { method: "POST" }),
};

/**
 * True when an API error means "this action needs Boursicote Pro" — i.e. the
 * gateway answered 402 with our `upgrade_required` code. The UI uses this to
 * open the upgrade dialog instead of showing a raw error toast.
 */
export function isUpgradeError(e: unknown): e is APIError {
  return (
    e instanceof APIError &&
    e.status === 402 &&
    typeof e.body === "object" &&
    e.body !== null &&
    (e.body as any).error === "upgrade_required"
  );
}

// ─────────────────────── stats helpers ───────────────────────

export type StatsWindow = "24h" | "48h" | "7d" | "14d";
export type StatsEnvelope<T> = { window: StatsWindow; data: T };

/** Unwrap the {window, data} envelope down to `data`. */
function unwrap<T>(env: StatsEnvelope<T>): T {
  return env.data;
}

export const apiBase = BASE;
export { APIError };

// ─────────────────────── LLM types + streaming ───────────────────────

export type LlmProvider = "openrouter" | "openai" | "anthropic";

export type LlmKeyStatus = {
  providers: LlmProvider[];
  defaults: Record<LlmProvider, string>;
  keys: Array<{
    provider: LlmProvider;
    model: string | null;
    last4: string | null;
    last_validated_at: string | null;
    updated_at: string;
  }>;
};

export type SummarizeKind = "thread" | "forum" | "news" | "stats";

export interface SummarizeHandlers {
  onMeta?: (m: { title: string; provider: string; model: string }) => void;
  onChunk: (text: string) => void;
  onDone?: () => void;
  onError?: (err: string) => void;
  signal?: AbortSignal;
}

/**
 * streamSummarize POSTs to /api/llm/summarize and consumes the SSE stream
 * (event: meta|chunk|done|error). Uses fetch+ReadableStream (not EventSource)
 * so we can send a POST body and the auth Bearer/cookie. Resolves when the
 * stream ends.
 */
export async function streamSummarize(
  body: { kind: SummarizeKind; id?: string | number; provider?: LlmProvider },
  handlers: SummarizeHandlers,
): Promise<void> {
  const token = getAuthToken();
  const res = await fetch(`${BASE}/api/llm/summarize`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "text/event-stream",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    credentials: "include",
    body: JSON.stringify(body),
    signal: handlers.signal,
  });

  if (!res.ok || !res.body) {
    let detail = "";
    try {
      const j = await res.json();
      detail = (j as any)?.detail ?? (j as any)?.error ?? "";
    } catch {
      /* noop */
    }
    handlers.onError?.(detail || `HTTP ${res.status}`);
    return;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    let idx: number;
    while ((idx = buf.indexOf("\n\n")) !== -1) {
      const rawEvent = buf.slice(0, idx);
      buf = buf.slice(idx + 2);
      let event = "message";
      let data = "";
      for (const line of rawEvent.split("\n")) {
        if (line.startsWith("event:")) event = line.slice(6).trim();
        else if (line.startsWith("data:")) data += line.slice(5).trim();
      }
      if (!data) continue;
      try {
        const payload = JSON.parse(data);
        if (event === "meta") handlers.onMeta?.(payload);
        else if (event === "chunk") handlers.onChunk(payload.text ?? "");
        else if (event === "done") handlers.onDone?.();
        else if (event === "error") handlers.onError?.(payload.error ?? "erreur");
      } catch {
        /* ignore malformed event */
      }
    }
  }
  handlers.onDone?.();
}
